import React, { useEffect, useState } from 'react';
import api from '../../services/api';

export default function InsuranceManagement() {
  const [plans, setPlans] = useState([]);
  const [claims, setClaims] = useState([]);
  const [claimTotal, setClaimTotal] = useState(0);
  const [tab, setTab] = useState('plans');
  const [showPlanForm, setShowPlanForm] = useState(false);
  const [planForm, setPlanForm] = useState({
    plan_name: '', plan_code: '', coverage_limit_kes: '',
    deductible_kes: 0, copay_percent: 0, pre_auth_required: true,
  });
  const [saving, setSaving] = useState(false);
  const [claimStatus, setClaimStatus] = useState('');
  const [error, setError] = useState('');
  const [claimError, setClaimError] = useState('');

  const loadPlans = async () => {
    try {
      const res = await api.get('/billing/insurance/plans');
      setPlans(res.data.data || []);
    } catch (e) {
      setError('Failed to load plans: ' + (e.response?.data?.detail || e.message));
    }
  };

  const loadClaims = async () => {
    try {
      const params = claimStatus ? `?status=${claimStatus}` : '';
      const res = await api.get(`/billing/insurance/claims${params}`);
      setClaims(res.data.data || []);
      setClaimTotal(res.data.total || 0);
    } catch (e) {
      setClaimError('Failed to load claims: ' + (e.response?.data?.detail || e.message));
    }
  };

  useEffect(() => {
    loadPlans();
  }, []);

  useEffect(() => {
    loadClaims();
  }, [claimStatus]);

  const savePlan = async () => {
    if (!planForm.plan_name.trim()) { setError('Plan name is required.'); return; }
    setSaving(true); setError('');
    try {
      await api.post('/billing/insurance/plans', {
        ...planForm,
        coverage_limit_kes: planForm.coverage_limit_kes ? Number(planForm.coverage_limit_kes) : null,
        deductible_kes: Number(planForm.deductible_kes),
        copay_percent: Number(planForm.copay_percent),
      });
      setShowPlanForm(false);
      setPlanForm({ plan_name: '', plan_code: '', coverage_limit_kes: '', deductible_kes: 0, copay_percent: 0, pre_auth_required: true });
      await loadPlans();
    } catch (e) {
      setError('Failed: ' + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const togglePlanActive = async (plan) => {
    try {
      await api.patch(`/billing/insurance/plans/${plan.plan_id}`, { is_active: !plan.is_active });
      await loadPlans();
    } catch (e) {
      setError('Failed to update plan: ' + (e.response?.data?.detail || e.message));
    }
  };

  const updateClaimStatus = async (claimId, newStatus) => {
    setClaimError('');
    try {
      await api.patch(`/billing/insurance/claims/${claimId}`, { claim_status: newStatus });
      await loadClaims();
    } catch (e) {
      setClaimError('Failed to update claim: ' + (e.response?.data?.detail || e.message));
    }
  };

  const CLAIM_STATUS_COLORS = {
    DRAFT: '#95a5a6', SUBMITTED: '#2980b9', APPROVED: '#27ae60',
    DENIED: '#c0392b', APPEALED: '#e67e22', PAID: '#27ae60',
  };

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', width: '100%', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#2b1b22' }}>Insurance Management</h1>
          <p style={{ margin: '4px 0 0', color: '#8b7681', fontSize: 13 }}>Plans, pre-authorizations and claim tracking</p>
        </div>
        {tab === 'plans' && (
          <button onClick={() => { setShowPlanForm(true); setError(''); }}
            style={{ background: '#7b1e3c', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            + New Plan
          </button>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '2px solid #ece3e7' }}>
        {[['plans', 'Insurance Plans'], ['claims', 'Claims']].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
            style={{ padding: '10px 24px', background: 'none', border: 'none', borderBottom: tab === k ? '2px solid #7b1e3c' : '2px solid transparent', marginBottom: -2, fontWeight: tab === k ? 800 : 600, color: tab === k ? '#7b1e3c' : '#8b7681', cursor: 'pointer', fontSize: 13 }}>
            {label}
          </button>
        ))}
      </div>

      {error && (
        <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>
          {error} <button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, color: '#b91c1c' }}>×</button>
        </div>
      )}

      {tab === 'plans' && (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #ece3e7', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8f5f6' }}>
                {['Plan Name', 'Code', 'Coverage Limit', 'Deductible', 'Copay %', 'Pre-Auth', 'Status', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#8b7681', fontSize: 11, textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {plans.length === 0 ? (
                <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: '#8b7681' }}>No insurance plans configured. Add one above.</td></tr>
              ) : plans.map((p, i) => (
                <tr key={p.plan_id} className="hhms-table-row" style={{ borderTop: i === 0 ? 'none' : '1px solid #f0ebed', opacity: p.is_active ? 1 : 0.6 }}>
                  <td style={{ padding: '10px 14px', fontWeight: 600 }}>{p.plan_name}</td>
                  <td style={{ padding: '10px 14px', color: '#555' }}>{p.plan_code || '—'}</td>
                  <td style={{ padding: '10px 14px' }}>{p.coverage_limit_kes ? `KES ${Number(p.coverage_limit_kes).toLocaleString()}` : 'Unlimited'}</td>
                  <td style={{ padding: '10px 14px' }}>KES {Number(p.deductible_kes || 0).toLocaleString()}</td>
                  <td style={{ padding: '10px 14px' }}>{p.copay_percent || 0}%</td>
                  <td style={{ padding: '10px 14px' }}>{p.pre_auth_required ? '✅ Required' : '—'}</td>
                  <td style={{ padding: '10px 14px' }}>
                    <span style={{ background: p.is_active ? '#f0faf4' : '#f8f9fa', color: p.is_active ? '#27ae60' : '#95a5a6', borderRadius: 999, padding: '2px 10px', fontSize: 11, fontWeight: 700 }}>
                      {p.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <button onClick={() => togglePlanActive(p)}
                      style={{ background: 'none', border: `1px solid ${p.is_active ? '#c0392b' : '#27ae60'}`, borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer', color: p.is_active ? '#c0392b' : '#27ae60', fontWeight: 600 }}>
                      {p.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'claims' && (
        <>
          {claimError && (
            <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>
              {claimError}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {['', 'DRAFT', 'SUBMITTED', 'APPROVED', 'DENIED', 'PAID'].map(s => (
              <button key={s} onClick={() => setClaimStatus(s)}
                style={{ padding: '5px 14px', borderRadius: 999, fontSize: 12, fontWeight: 600, border: `1px solid ${claimStatus === s ? '#7b1e3c' : '#ddd'}`, background: claimStatus === s ? '#7b1e3c' : '#fff', color: claimStatus === s ? '#fff' : '#555', cursor: 'pointer' }}>
                {s || 'All'}
              </button>
            ))}
            <span style={{ marginLeft: 'auto', fontSize: 12, color: '#8b7681', alignSelf: 'center' }}>{claimTotal} claims</span>
          </div>
          <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #ece3e7', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8f5f6' }}>
                  {['Patient', 'Plan', 'Pre-Auth', 'Status', 'Approved Amount', 'Submitted', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#8b7681', fontSize: 11, textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {claims.length === 0 ? (
                  <tr><td colSpan={7} style={{ padding: 40, textAlign: 'center', color: '#8b7681' }}>No claims found.</td></tr>
                ) : claims.map((c, i) => (
                  <tr key={c.claim_id} className="hhms-table-row" style={{ borderTop: i === 0 ? 'none' : '1px solid #f0ebed' }}>
                    <td style={{ padding: '10px 14px', fontWeight: 600 }}>{c.patient_name}</td>
                    <td style={{ padding: '10px 14px', color: '#555' }}>{c.plan_name || '—'}</td>
                    <td style={{ padding: '10px 14px', color: '#555' }}>{c.pre_auth_code || '—'}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{ background: (CLAIM_STATUS_COLORS[c.claim_status] || '#ccc') + '22', color: CLAIM_STATUS_COLORS[c.claim_status] || '#555', borderRadius: 999, padding: '2px 10px', fontSize: 11, fontWeight: 700 }}>
                        {c.claim_status}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px' }}>{c.approved_amount ? `KES ${Number(c.approved_amount).toLocaleString()}` : '—'}</td>
                    <td style={{ padding: '10px 14px', color: '#8b7681', fontSize: 12 }}>{c.submitted_at ? new Date(c.submitted_at).toLocaleDateString() : '—'}</td>
                    <td style={{ padding: '10px 14px', display: 'flex', gap: 6 }}>
                      {c.claim_status === 'DRAFT' && (
                        <button onClick={() => updateClaimStatus(c.claim_id, 'SUBMITTED')}
                          style={{ background: '#2980b9', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>Submit</button>
                      )}
                      {c.claim_status === 'SUBMITTED' && (
                        <button onClick={() => updateClaimStatus(c.claim_id, 'APPROVED')}
                          style={{ background: '#27ae60', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>Approve</button>
                      )}
                      {c.claim_status === 'SUBMITTED' && (
                        <button onClick={() => updateClaimStatus(c.claim_id, 'DENIED')}
                          style={{ background: '#c0392b', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>Deny</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Plan form modal */}
      {showPlanForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 32, width: 480, maxWidth: '90vw' }}>
            <h3 style={{ margin: '0 0 20px', color: '#2b1b22' }}>New Insurance Plan</h3>
            {error && <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '8px 12px', borderRadius: 6, marginBottom: 14, fontSize: 12 }}>{error}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, marginBottom: 4, color: '#555' }}>Plan Name *</label>
                <input value={planForm.plan_name} onChange={e => setPlanForm(f => ({ ...f, plan_name: e.target.value }))}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, marginBottom: 4, color: '#555' }}>Plan Code</label>
                <input value={planForm.plan_code} onChange={e => setPlanForm(f => ({ ...f, plan_code: e.target.value }))}
                  placeholder="e.g. NHIF-001"
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, marginBottom: 4, color: '#555' }}>Coverage Limit (KES)</label>
                <input type="number" value={planForm.coverage_limit_kes} onChange={e => setPlanForm(f => ({ ...f, coverage_limit_kes: e.target.value }))}
                  placeholder="Leave blank for unlimited"
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, marginBottom: 4, color: '#555' }}>Deductible (KES)</label>
                <input type="number" value={planForm.deductible_kes} onChange={e => setPlanForm(f => ({ ...f, deductible_kes: e.target.value }))}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, marginBottom: 4, color: '#555' }}>Patient Copay (%)</label>
                <input type="number" min="0" max="100" value={planForm.copay_percent} onChange={e => setPlanForm(f => ({ ...f, copay_percent: e.target.value }))}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13, boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 20 }}>
                <input type="checkbox" id="pre-auth-chk" checked={planForm.pre_auth_required}
                  onChange={e => setPlanForm(f => ({ ...f, pre_auth_required: e.target.checked }))} />
                <label htmlFor="pre-auth-chk" style={{ fontSize: 13, fontWeight: 600, color: '#2b1b22', cursor: 'pointer' }}>Pre-auth Required</label>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button onClick={savePlan} disabled={saving}
                style={{ flex: 1, padding: '10px 0', background: '#7b1e3c', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Saving…' : 'Save Plan'}
              </button>
              <button onClick={() => { setShowPlanForm(false); setError(''); }}
                style={{ padding: '10px 20px', background: '#f8f5f6', border: '1px solid #ddd', borderRadius: 8, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
